<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import {
  ArrowLeftIcon,
  ShoppingBagIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/vue/24/outline'
import { useCartStore } from '~/store/CartStore'
import { useMainStore } from '~/store/MainStore'
import { useOrderStore } from '~/store/OrderStore'
import { getFormattedAddress } from '~/classes/Utils'
import type { BreadcrumbItem, NavigationItem } from '~/definitions/AllDefinitions.js'
import Breadcrumbs from '~/components/Navigation/Breadcrumbs.vue'
import CurrentUser from '~/components/User/CurrentUser.vue'
import { DELIVERY_METHODS } from '~/definitions/enums'

interface NavbarInterface {
  displayBack?: boolean
  disableCurrentUser?: boolean
  displayBreadcrumbs?: boolean
  displayDeliveryMethods?: boolean
  displaySearch?: boolean
  displaySubNavigation?: boolean
  navigationItems?: NavigationItem[]
  breadcrumbItems?: BreadcrumbItem[]
}

withDefaults(defineProps<NavbarInterface>(), {
  displayBack: false,
  disableCurrentUser: false,
  displayBreadcrumbs: false,
  displayDeliveryMethods: false,
  displaySearch: false,
  displaySubNavigation: false,
  navigationItems() {
    return [] as NavigationItem[]
  },
  breadcrumbItems() {
    return [] as BreadcrumbItem[]
  },
})
const emit = defineEmits(['open-delivery-methods', 'search-input'])
const searchText = ref('')
const cartStore = useCartStore()
const orderStore = useOrderStore()
const route = useRoute()
const { order } = storeToRefs(orderStore)
const {
  currentLocation,
  deliveryMethods,
  hasOrderingCapability,
  isOrderingEnabled,
  navIsLightColor,
  rootData,
} = storeToRefs(useMainStore())
const { cartCount } = storeToRefs(cartStore)
const { logo, name } = rootData.value
const { t } = useI18n()
const disableCart = computed(() => {
  return !isOrderingEnabled.value || !cartCount.value || route.name === 'checkout'
})
const logoImage = computed(() => {
  if (!logo.original) {
    return null
  }
  return typeof logo.sizes === 'undefined' || !logo.sizes
    ? logo.original
    : logo.type === 'square'
      ? logo.sizes.s64
        ? logo.sizes.s64
        : logo.original
      : logo.sizes.landscapeLogo
        ? logo.sizes.landscapeLogo
        : logo.original
})
const logoImageSrcset = computed(() => {
  if (!logo.original) {
    return undefined
  }
  return typeof logo.sizes === 'undefined' || !logo.sizes || typeof logo.sizes2x === 'undefined' || !logo.sizes2x
    ? undefined
    : logo.type === 'square'
      ? logo.sizes.s64 && logo.sizes2x.s64
        ? `${logo.sizes2x.s64} 2x`
        : undefined
      : logo.sizes.landscapeLogo && logo.sizes2x.landscapeLogo
        ? `${logo.sizes2x.landscapeLogo} 2x`
        : undefined
})
function openCart() {
  cartStore.setCartIsOpen(true)
}
function openDeliveryMethods() {
  emit('open-delivery-methods')
}
const deliveryName = computed(() => {
  return order.value.firstName || order.value.lastName
    ? order.value.firstName && order.value.lastName
      ? `${order.value.firstName} ${order.value.lastName}`
      : order.value.firstName
        ? order.value.firstName
        : order.value.lastName
    : ''
})
const formattedAddress = computed(() => {
  return deliveryName.value
    ? `${deliveryName.value} @ ${getFormattedAddress(order.value)}`
    : getFormattedAddress(order.value)
})
function handleUpdateSearchText(ev?: any) {
  if (ev) {
    emit('search-input', ev.target.value)
  }
}
onMounted(() => {
  cartStore.setCartIsOpen(false)
})
</script>

<template>
  <nav
    id="navbar"
    class="z-40 w-full bg-navbar shadow-sm"
  >
    <div class="tw-container">
      <div class="relative flex sm:h-16 sm:py-0 py-2 items-center justify-between sm:flex-nowrap flex-wrap">
        <div
          v-if="displaySearch"
          class="lg:mr-5 mr-1.5 inline-flex items-center grow"
        >
          <div class="inline-flex items-center justify-between w-full text-left md:min-w-[300px]">
            <div
              :class="{
                'bg-slate-800/20': navIsLightColor,
                'bg-white/20': !navIsLightColor,
              }"
              class="search-input justify-start"
            >
              <div class="grow-0 mr-2">
                <MagnifyingGlassIcon
                  class="h-6 w-6 text-navbar-contrast"
                  aria-hidden="true"
                />
              </div>
              <input
                v-model="searchText"
                :placeholder="t('SEARCH_FOOD')"
                @input="handleUpdateSearchText"
              >
            </div>
          </div>
        </div>
        <div
          :class="{
            'sm:inline-flex sm:justify-start justify-center sm:grow sm:mb-0 mb-2': displaySearch,
            'inline-flex justify-start': !displaySearch,
          }"
          class="items-center sm:justify-start justify-center sm:grow
            text-navbar-contrast md:text-xl font-semibold text-center sm:text-base text-sm"
        >
          <router-link
            to="/"
            class="inline-flex items-center"
          >
            <ArrowLeftIcon
              v-if="displayBack"
              class="h-5 w-5 mr-2"
            />
            <h1 v-if="!logoImage">
              {{ name }}
            </h1>
            <img
              v-else
              :src="logoImage"
              :srcset="logoImageSrcset"
              :alt="logo.altText || name"
              :class="{ 'h-16 w-16': logo.type === 'square', 'h-12': logo.type !== 'square' }"
            >
          </router-link>
        </div>
        <div class="items-center relative inline-flex">
          <div class="inline-flex items-center relative grow-0">
            <ClientOnly>
              <current-user :disable="disableCurrentUser" />
            </ClientOnly>
            <ClientOnly>
              <div
                v-if="hasOrderingCapability"
                class="border-l border-navbar-contrast ml-1.5 lg:pl-5 pl-1.5 flow-root lg:ml-5"
              >
                <button
                  type="button"
                  :disabled="disableCart"
                  class="group -m-2 flex items-center p-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  @click="openCart()"
                >
                  <ShoppingBagIcon
                    class="h-6 w-6 flex-shrink-0 text-navbar-contrast group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                  <span class="lg:ml-2 ml-0.5 text-sm font-medium text-navbar-contrast group-hover:text-gray-800">
                    {{ $filters.formatNumber(cartCount) }}
                  </span>
                  <span class="sr-only">items in cart, view bag</span>
                </button>
              </div>
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="hasOrderingCapability && displayDeliveryMethods"
      class="bg-tw-2 text-slate-700 dark:text-white w-full"
    >
      <div class="tw-container sm:h-14 h-10">
        <div class="flex flex-row justify-center items-center h-full text-sm">
          <span v-if="currentLocation?.id && !isOrderingEnabled">
            {{ t('NOT_ACCEPTING_ORDERS') }}
          </span>
          <span v-else-if="order.deliveryMethod === DELIVERY_METHODS.PICKUP && currentLocation && currentLocation.id">
            {{ t('PICKUP_FROM_X', { value: currentLocation.name || currentLocation.formattedAddress }) }}
          </span>
          <span v-else-if="order.deliveryMethod === DELIVERY_METHODS.PICKUP">
            {{ t('PICKUP') }}
          </span>
          <span v-else-if="order.deliveryMethod === DELIVERY_METHODS.DELIVERY">
            {{ t('DELIVERY_TO_X', { value: formattedAddress }) }}
          </span>
          <span v-else>
            {{ t('PICKUP_DELIVERY') }}
          </span>
          <button
            v-if="isOrderingEnabled && deliveryMethods.length > 1"
            class="ml-2 tw-contrast-button"
            @click="openDeliveryMethods()"
          >
            <PencilSquareIcon class="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="displayBreadcrumbs || displaySubNavigation"
      class="bg-tw-2 text-tw-2 w-full"
    >
      <div class="tw-container">
        <breadcrumbs
          v-if="displayBreadcrumbs"
          :items="breadcrumbItems"
        />
        <nav
          v-else
          class="sm:flex overflow-x-auto py-4 hidden"
        >
          <ul
            role="list"
            class="flex min-w-full flex-none gap-x-6 text-sm font-semibold leading-6"
          >
            <li
              v-for="(item, itemIdx) in navigationItems"
              :key="itemIdx"
            >
              <router-link
                :class="{
                  'text-accent dark:text-white': item.path === route.path,
                  'hover:text-accent hover:dark:text-accent-400': item.path !== route.path,
                }"
                :to="item.path"
              >
                {{ item.label }}
              </router-link>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </nav>
</template>
